const API_BASE = '';

function getToken(): string | null {
  return localStorage.getItem('openclaw_token');
}

export function setToken(token: string) {
  localStorage.setItem('openclaw_token', token);
}

export function clearToken() {
  localStorage.removeItem('openclaw_token');
}

export function isAuthenticated(): boolean {
  return !!getToken();
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
    public requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
    });
  } catch {
    throw new ApiError(0, 'Network request failed');
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const contentType = res.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const payload = isJson ? await res.json().catch(() => null) : await res.text().catch(() => '');

  if (res.status === 401) {
    clearToken();
    window.location.href = '/401';
    throw new ApiError(401, 'Unauthorized');
  }

  if (res.status === 403) {
    window.location.href = '/403';
    throw new ApiError(403, 'Forbidden');
  }

  if (!res.ok) {
    const message = typeof payload === 'string'
      ? payload
      : payload?.error || payload?.message || `Request failed with status ${res.status}`;
    throw new ApiError(res.status, message, payload?.code, payload?.request_id || payload?.requestId);
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
